import { useMemo, useSyncExternalStore } from "react";
import type { TargetAndTransition, Transition } from "framer-motion";
import { motion, type MotionConfig, type SpringName } from "./motion";

/**
 * React-side motion utilities.
 *
 * Kept apart from `motion.ts` so the pure token object stays importable
 * without React.
 */

// ---------------------------------------------------------------------------
// usePrefersReducedMotion
// ---------------------------------------------------------------------------

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function subscribeToReducedMotion(notify: () => void): () => void {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
        return () => undefined;
    }
    const mql = window.matchMedia(REDUCED_MOTION_QUERY);
    mql.addEventListener("change", notify);
    return () => mql.removeEventListener("change", notify);
}

function getReducedMotionSnapshot(): boolean {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
        return false;
    }
    return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

function getReducedMotionServerSnapshot(): boolean {
    return false;
}

/**
 * `true` when the user prefers reduced motion. Stays in sync when the OS
 * preference changes while the app is open.
 */
export function usePrefersReducedMotion(): boolean {
    return useSyncExternalStore(
        subscribeToReducedMotion,
        getReducedMotionSnapshot,
        getReducedMotionServerSnapshot,
    );
}

// ---------------------------------------------------------------------------
// useReducedMotionSafe
// ---------------------------------------------------------------------------

/**
 * Resolve a spring name to a motion config for the current user. Under
 * reduced motion every spring collapses to the `instant` tween.
 */
export function useReducedMotionSafe(name: SpringName): MotionConfig {
    const reduced = usePrefersReducedMotion();
    if (reduced) {
        return motion.springs.instant;
    }
    return motion.springs[name];
}

// ---------------------------------------------------------------------------
// useOverlayMotion / useListItemMotion
// ---------------------------------------------------------------------------

export interface PresenceMotionProps {
    readonly initial: TargetAndTransition;
    readonly animate: TargetAndTransition;
    readonly exit: TargetAndTransition;
    readonly transition: Transition;
}

export interface EntranceMotionProps {
    readonly initial: TargetAndTransition;
    readonly animate: TargetAndTransition;
    readonly transition: Transition;
}

export type OverlayKind = keyof typeof motion.overlay.scale;

/**
 * Compositor-only motion policy.
 *
 * Every presence recipe below animates `opacity` and the composed
 * `transform` string, and nothing else. framer-motion hands `opacity`,
 * `clipPath`, `filter`, and the literal `transform` key to the Web
 * Animations API, so the compositor runs them. `x`, `y`, and `scale` as
 * separate props run on the main thread and write inline `style.transform`
 * every frame; a per-frame scale change re-rasters the whole layer, which
 * is the stutter this policy removes. Do not add `x`, `y`, `scale`,
 * `width`, or `height` to a recipe here; compose the transform string.
 */
export const composeTransform = (translateYPx: number, scale: number): string =>
    `translateY(${String(translateYPx)}px) scale(${String(scale)})`;

/** Resting transform. Explicit so the WAAPI keyframes share one structure. */
export const REST_TRANSFORM = composeTransform(0, 1);

/**
 * Persistent-layer policy. Spread into the `style` of the element that
 * carries a presence recipe (overlay card, scrim, toast).
 *
 * Chrome promotes an element to its own compositor layer while an opacity or
 * transform animation runs and drops the layer when the animation ends. The
 * two states rasterize text differently (grayscale anti-aliasing on the
 * layer, subpixel anti-aliasing in the page), so the last frame of an enter
 * and the first frame of an exit repaint every glyph of the overlay while
 * opacity barely moves. Measured on the queue row menu in a production
 * build: ~2000 panel pixels shift by more than 16/255 on a frame whose
 * opacity changes by 0.003; with the layer held, 0 pixels. Holding the layer
 * also has it rasterized before the animation starts, so the fade begins on
 * the compositor's next frame and keeps running when the main thread is
 * busy after the click.
 *
 * Overlays animate `transform`, and a layer that may transform never gets
 * subpixel text, so the overlay's text is grayscale for its whole lifetime:
 * one rendering, no switch. One layer per overlay. Do not apply per list
 * row: a layer per row costs memory and mixes the two text renderings inside
 * one list.
 */
export const presenceLayerStyle = { willChange: "opacity, transform" } as const;

/**
 * Persistent-layer policy for in-flow content that only fades (`ContentSwap`
 * layers). Holding the layer removes the end-of-fade repaint as above. The
 * layer animates `opacity` only and paints an opaque background, so Chrome
 * keeps subpixel text on it: the content rasterizes exactly as the page
 * around it, during the fade and at rest. Measured on the queue list: with
 * `will-change: opacity` alone the layer is grayscale at rest (chroma 226 vs
 * 3825 in the page); with the opaque background it matches the page (3825)
 * and no frame repaints at rest. The background must be the colour of the
 * host the swap sits on; a mismatch shows as a rectangle.
 */
export const swapLayerStyle = (background: string) =>
    ({ willChange: "opacity", background }) as const;

function reducedFade(): PresenceMotionProps {
    const fade: Transition = { duration: motion.reduced.duration };
    return {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0, transition: fade },
        transition: fade,
    };
}

/**
 * Enter/exit props for anything that floats: 160ms in (fade, 4px rise, scale
 * from `motion.overlay.scale[kind]`), 110ms out. The rise and scale travel
 * as one `transform` string so the browser animates them off the main
 * thread. Spread onto the `motion.*` element that is the direct child of an
 * `AnimatePresence`; set `transformOrigin` on that element to the anchor
 * edge so the scale grows from where the overlay came from. Reduced motion
 * collapses to a 100ms crossfade.
 */
export function useOverlayMotion(kind: OverlayKind = "popover"): PresenceMotionProps {
    const reduced = usePrefersReducedMotion();
    return useMemo<PresenceMotionProps>(() => {
        if (reduced) return reducedFade();
        const { rise, enter, exit, scale } = motion.overlay;
        const from = composeTransform(rise, scale[kind]);
        return {
            initial: { opacity: 0, transform: from },
            animate: { opacity: 1, transform: REST_TRANSFORM },
            exit: {
                opacity: 0,
                transform: from,
                transition: { duration: exit.duration, ease: exit.ease },
            },
            transition: { duration: enter.duration, ease: enter.ease },
        };
    }, [kind, reduced]);
}

/** Scrim behind a dialog: 120ms opacity, nothing else. */
export function useScrimMotion(): PresenceMotionProps {
    const reduced = usePrefersReducedMotion();
    return useMemo<PresenceMotionProps>(() => {
        const duration = reduced ? motion.reduced.duration : motion.scrim.duration;
        const fade: Transition = { duration, ease: "linear" };
        return {
            initial: { opacity: 0 },
            animate: { opacity: 1 },
            exit: { opacity: 0, transition: fade },
            transition: fade,
        };
    }, [reduced]);
}

// ---------------------------------------------------------------------------
// insertionMotion / useListInsertion
// ---------------------------------------------------------------------------

export interface InsertionMotionProps extends PresenceMotionProps {
    /** Spread onto the same element. Clips the row while its height grows. */
    readonly style: { readonly overflow: "hidden" };
}

/** Animation target for a growing row: its measured px, or `"auto"` once settled. */
export type InsertionHeight = number | "auto";

/**
 * A row that joins a list the user is already looking at (add member, add
 * note, realtime insert): height 0 → the row's measured px plus a fade over
 * 220ms, ease-out, no stagger. Exit reverses it in the same time. The caller
 * measures the row once in a layout effect and passes the px, so only the
 * inserting row lays out and the animation never resolves `height: auto`
 * mid-flight; it passes `"auto"` after the row has settled so content
 * changes flow again. Height is the one layout property a recipe here may
 * animate: a row that grows must move its siblings. Pure form of
 * `useListInsertion` for code that already knows the motion preference.
 */
export function insertionMotion(reduced: boolean, height: InsertionHeight): InsertionMotionProps {
    const style = { overflow: "hidden" } as const;
    if (reduced) {
        const fade: Transition = { duration: motion.reduced.duration };
        return {
            initial: { opacity: 0 },
            animate: { opacity: 1 },
            exit: { opacity: 0, transition: fade },
            transition: fade,
            style,
        };
    }
    const { duration, ease } = motion.insertion;
    const tween: Transition = { duration, ease };
    return {
        initial: { height: 0, opacity: 0 },
        animate: { height, opacity: 1 },
        exit: { height: 0, opacity: 0, transition: tween },
        transition: tween,
        style,
    };
}

/** Hook form of `insertionMotion`. Spread onto a `motion.*` row inside the list's `AnimatePresence`. */
export function useListInsertion(height: InsertionHeight): InsertionMotionProps {
    const reduced = usePrefersReducedMotion();
    return useMemo(() => insertionMotion(reduced, height), [reduced, height]);
}

/**
 * Arrival props for the item at `index` in a list. With `stagger` the row
 * fades in with a 4px rise, delayed 30ms per index and capped after
 * `maxStaggered` items. Without it the row only crossfades: no rise, no
 * delay. Under the entrance policy (below) data-driven lists pass
 * `stagger: false` and render inside `<AnimatePresence initial={false}>`, so
 * rows on screen when the list fills animate nothing and only a row that
 * joins a visible list fades in. Items carry no exit; the parent list owns
 * the single `AnimatePresence`.
 */
export function useListItemMotion(index: number, stagger = true): EntranceMotionProps {
    const reduced = usePrefersReducedMotion();
    return useMemo<EntranceMotionProps>(() => {
        if (reduced || !stagger) {
            return {
                initial: { opacity: 0 },
                animate: { opacity: 1 },
                transition: { duration: motion.reduced.duration },
            };
        }
        const { rise, duration, stagger: perItem, maxStaggered } = motion.listItem;
        const slot = Math.max(0, Math.min(index, maxStaggered - 1));
        return {
            initial: { opacity: 0, transform: composeTransform(rise, 1) },
            animate: { opacity: 1, transform: REST_TRANSFORM },
            transition: { duration, delay: slot * perItem, ease: motion.curves.standard },
        };
    }, [index, reduced, stagger]);
}

// ---------------------------------------------------------------------------
// Entrance policy / useHydrationCrossfade
// ---------------------------------------------------------------------------

/**
 * Entrance policy for data-driven surfaces.
 *
 * A per-row entrance plays only when a list gains rows while it is already
 * on screen with content: a real insert (`useListInsertion`, `InsertedRow`,
 * `useNewRowDetector`) or a row that joins a visible list (`useListItemMotion`
 * without stagger inside `<AnimatePresence initial={false}>`).
 *
 * A skeleton → content swap never staggers. The skeleton fades out and the
 * content fades in as one unit: a single opacity crossfade on the swap host,
 * `motion.hydration.duration` long, 0 under reduced motion. Rows present at
 * that swap carry no `initial`. `ContentSwap` (primitives) applies this hook,
 * so every list that renders through it gets the policy by construction.
 * Skeletons keep the row geometry of the content they stand in for, so the
 * crossfade does not shift layout.
 */
export interface HydrationCrossfade {
    /** Seconds. 0 under reduced motion. */
    readonly duration: number;
    /** Props for the layer that arrives. */
    readonly enter: EntranceMotionProps;
    /** `exit` target for the layer that leaves. */
    readonly exit: TargetAndTransition;
}

export function useHydrationCrossfade(): HydrationCrossfade {
    const reduced = usePrefersReducedMotion();
    return useMemo<HydrationCrossfade>(() => {
        const duration = reduced ? 0 : motion.hydration.duration;
        const transition: Transition = { duration, ease: motion.hydration.ease };
        return {
            duration,
            enter: { initial: { opacity: 0 }, animate: { opacity: 1 }, transition },
            exit: { opacity: 0, transition },
        };
    }, [reduced]);
}
